import { createSlice } from "@reduxjs/toolkit"
import {
  addBanner,
  addSlide,
  deleteBanner,
  deleteSlide,
  getAllBanners,
  getBannerDetails,
  getSlideDetails,
  updateBanner,
  updateBannerStatus,
  updateSlide,
  updateSlideStatus,
} from "store/actions/bannerAction"

const initialState = {
  loading: true,
  error: null,
  isLoading: false,
  actionType: null,
  message: null,
  itemsPerPage: 10,
  totalCount: null,
  totalPages: null,
  currentPage: 1,
  isUpdating: false,
  bannerLists: [],
  bannerDetails: {},
  slideDetails: {},
}

const usersSlice = createSlice({
  name: "name", //
  initialState,
  reducers: {},
  extraReducers: builder => {
    // FETCH ALL BANNERS
    builder
      .addCase(getAllBanners.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(getAllBanners.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          const { totalCount, totalPages, currentPage } = action.payload
          state.bannerLists = data
          state.totalCount = totalCount
          state.totalPages = totalPages
          state.currentPage = currentPage
        }
      })
      .addCase(getAllBanners.rejected, (state, action) => {
        state.loading = false
      })

    // ADD BANNER
    builder
      .addCase(addBanner.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
        state.message = null
      })
      .addCase(addBanner.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, data } = action.payload
        if (status) {
          if (data) {
            state.bannerLists.push(data)
          }
          state.actionType = "BANNER_ADDED_SUCCESS"
          state.message = action.payload.msg
          action.meta.arg.handleClose()
          if (state.totalCount !== null) {
            state.totalCount += 1
          }
        } else {
          state.error = action.payload.msg
          state.actionType = "BANNER_ADDED_FAILED"
        }
      })
      .addCase(addBanner.rejected, (state, action) => {
        state.isLoading = false
      })

    // DELETE BANNER
    builder
      .addCase(deleteBanner.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, msg } = action.payload
        const { id } = action.meta.arg
        if (status) {
          state.bannerLists = state.bannerLists.filter(item => item._id !== id)
          state.error = null
          state.actionType = "BANNER_DELETE_SUCCESS"
          state.message = msg
          action.meta.arg.setShowDeleteModal(false)
          if (state.totalCount !== null) {
            state.totalCount -= 1
          }
        } else {
          state.actionType = "BANNER_DELETE_FAILED"
          state.error = msg
        }
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.isLoading = false
      })

      //   GET BANNER DETAILS
      .addCase(getBannerDetails.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(getBannerDetails.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, data } = action.payload
        if (status) {
          state.bannerDetails = data
          state.actionType = "GET_BANNER_DETAILS_SUCCESS"
        }
      })
      .addCase(getBannerDetails.rejected, (state, action) => {
        state.isLoading = false
        state.actionType = "GET_BANNER_DETAILS_FAILED"
      })

    //   UPDATE BANNER
    builder
      .addCase(updateBanner.pending, state => {
        state.error = null
        state.isUpdating = true
        state.actionType = null
        state.message = null
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, name, nameAr } = action.meta.arg
          state.actionType = "BANNER_UPDATED_SUCCESS"
          state.bannerLists = state.bannerLists.map(banner =>
            banner._id === id ? { ...banner, name, nameAr } : banner
          )
          state.message = action.payload.msg
          action.meta.arg.handleClose()
        } else {
          state.error = action.payload.msg
          state.actionType = "BANNER_UPDATED_FAILED"
        }
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.isUpdating = false
      })

    // UPDATE BANNER STATUS
    builder
      .addCase(updateBannerStatus.pending, state => {
        state.error = null
        state.actionType = null
        state.isUpdating = true
      })
      .addCase(updateBannerStatus.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, status } = action.meta.arg

          // Get the countryCode of the banner being updated
          const updatedBanner = state.bannerLists.find(
            banner => banner._id === id
          )
          const updatedCountryCode = updatedBanner
            ? updatedBanner.countryCode
            : null
          // Deactivate all other banners in the same country and update the status of the clicked banner
          state.bannerLists = state.bannerLists.map(banner =>
            banner.countryCode === updatedCountryCode
              ? banner._id === id
                ? { ...banner, status: Number(status) }
                : { ...banner, status: 0 }
              : banner
          )

          state.message = action.payload.msg
          state.actionType = "BANNER_STATUS_UPDATE_SUCCESS"
        } else {
          state.actionType = "BANNER_STATUS_UPDATE_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(updateBannerStatus.rejected, (state, action) => {
        state.isUpdating = false
        state.rolesList = [] //
      })

    // SLIDES REDUCER
    // ADD SLIDE
    builder
      .addCase(addSlide.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
        state.message = null
      })
      .addCase(addSlide.fulfilled, (state, action) => {
        state.isLoading = false
        const { status } = action.payload
        if (status) {
          state.actionType = "SLIDE_ADDED_SUCCESS"
          state.message = action.payload.msg
          // action.meta.arg.navigate('/banners')
        } else {
          state.error = action.payload.msg
          state.actionType = "SLIDE_ADDED_FAILED"
        }
      })
      .addCase(addSlide.rejected, (state, action) => {
        state.isLoading = false
      })

    // UPDATE Slide STATUS
    builder
      .addCase(updateSlideStatus.pending, state => {
        state.error = null
        state.actionType = null
        state.isUpdating = true
      })
      .addCase(updateSlideStatus.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          const { id, status, bannerId } = action.meta.arg
          // Update slide status of the corresponding banner using bannerId
          state.bannerLists = state.bannerLists.map(banner => {
            if (banner._id === bannerId) {
              banner.slides = banner.slides.map(slide => {
                if (slide._id === id) {
                  slide.status = Number(status) // Update the status of the slide
                }
                return slide
              })
            }
            return banner
          })
          state.actionType = "SLIDE_STATUS_UPDATE_SUCCESS"
          state.message = action.payload.msg
        } else {
          state.actionType = "SLIDE_STATUS_UPDATE_FAILED"
          state.error = action.payload.msg
        }
      })
      .addCase(updateSlideStatus.rejected, (state, action) => {
        state.isUpdating = false
      })

    // DELETE SLIDE
    builder
      .addCase(deleteSlide.pending, state => {
        state.error = null
        state.isLoading = true
        state.actionType = null
      })
      .addCase(deleteSlide.fulfilled, (state, action) => {
        state.isLoading = false
        const { status, msg } = action.payload
        const { id, selectedSlideBannerId, setShowSlideDeleteModal } =
          action.meta.arg
        if (status) {
          // Remove the slide with the corresponding slideId from bannerLists
          state.bannerLists = state.bannerLists.map(banner => {
            if (banner._id === selectedSlideBannerId) {
              banner.slides = banner.slides.filter(slide => slide._id !== id)
            }
            return banner
          })

          state.actionType = "SLIDE_DELETE_SUCCESS"
          state.message = msg
          setShowSlideDeleteModal(false)
        } else {
          state.actionType = "SLIDE_DELETE_FAILED"
          state.error = msg
        }
      })
      .addCase(deleteSlide.rejected, (state, action) => {
        state.isLoading = false
      })

    // GET SLIDE DETAILS
    builder
      .addCase(getSlideDetails.pending, state => {
        state.loading = true
        state.error = null
        state.actionType = null
      })
      .addCase(getSlideDetails.fulfilled, (state, action) => {
        state.loading = false
        const { status, data } = action.payload
        if (status) {
          state.slideDetails = data
        }
      })
      .addCase(getSlideDetails.rejected, (state, action) => {
        state.loading = false
      })

    // UPDATE SLIDE DETAILS
    builder
      .addCase(updateSlide.pending, state => {
        state.error = null
        state.isUpdating = true
        state.actionType = null
        state.message = null
      })
      .addCase(updateSlide.fulfilled, (state, action) => {
        state.isUpdating = false
        const { status } = action.payload
        if (status) {
          state.actionType = "SLIDE_UPDATED_SUCCESS"
          state.message = action.payload.msg
        } else {
          state.error = action.payload.msg
          state.actionType = "SLIDE_UPDATED_FAILED"
        }
      })
      .addCase(updateSlide.rejected, (state, action) => {
        state.isUpdating = false
      })
  },
})

export default usersSlice.reducer
